import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, DOCUMENT, inject } from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivatedRoute, Router } from '@angular/router';
import { provideTranslocoScope, TranslocoDirective } from '@jsverse/transloco';
import { nonNullable } from '@rma/generic/rxjs/operators/non-nullable';
import { rmaShareReplay } from '@rma/generic/rxjs/operators/rma-share-replay';
import { CurrentUser, PublicUserContextService } from '@rma/public/accounts/user';
import { TokenProviderService } from '@rma/public/generic/util/http';
import { format } from 'date-fns';
import { catchError, map, Observable, of, startWith, Subject, switchMap, take } from 'rxjs';
import { ActivationTokenDetails } from '../activation-token-details';
import { ActivationService } from '../data-access-agency-activation/activation.service';
import { AgentActivationComponent } from '../ui-agent-activation/agent-activation.component';

@Component({
  selector: 'rma-activation-token',
  templateUrl: './activation-token.component.html',
  styleUrls: ['./activation-token.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AgentActivationComponent, AsyncPipe, MatProgressSpinnerModule, TranslocoDirective],
  providers: [provideTranslocoScope('activation')],
})
export class ActivationTokenComponent {
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly activationService = inject(ActivationService);
  private readonly document = inject<Document>(DOCUMENT);
  private readonly router = inject(Router);
  private readonly tokenProviderService = inject(TokenProviderService);
  private readonly userService = inject(PublicUserContextService);

  public activationTokenDetails$: Observable<ActivationTokenDetails | null>;
  public token$: Observable<string | undefined>;
  public error$: Observable<string>;
  public pending$: Observable<boolean>;
  private error = new Subject<string>();
  private pending = new Subject<boolean>();

  constructor() {
    this.error$ = this.error.asObservable();
    this.pending$ = this.pending.asObservable().pipe(startWith(false));

    this.token$ = this.activatedRoute.queryParams.pipe(
      take(1),
      map(({ token }) => token),
      rmaShareReplay(),
    );

    this.token$.pipe(take(1)).subscribe((token) => {
      if (!token) {
        this.router.navigate(['/']);
      }
    });

    this.activationTokenDetails$ = this.token$.pipe(
      nonNullable(),
      switchMap((token) => this.activationService.getDetailsFromToken(token)),
      catchError((error) => {
        this.error.next(error?.error?.message ?? error.message);
        return of(null);
      }),
      rmaShareReplay(),
    );
  }

  public activateProfile() {
    this.pending.next(true);

    //Hubspot cannot make tokens with redirect urls, so this is provided as an override
    const redirectUrlOverride = this.activatedRoute.snapshot.queryParamMap.get('redirectUrl');
    this.token$
      .pipe(
        nonNullable(),
        switchMap((token) => this.activationService.activateFromToken(token, true)),
        switchMap((result) =>
          this.persistJwtAuth(result.userToken).pipe(
            map((_) => ({
              redirectUrl: redirectUrlOverride ?? result.redirectUrl,
            })),
          ),
        ),
      )
      .subscribe({
        next: (result) => {
          this.pending.next(false);
          this.document.location.href = result?.redirectUrl ?? `/dashboard?claimedOn=${format(new Date(), 'yyyy-MM-dd')}`;
        },
        error: (error) => {
          this.pending.next(false);
          this.error.next(error?.error?.message ?? error.message);
        },
      });
  }

  private persistJwtAuth(userToken: string): Observable<CurrentUser | null> {
    this.tokenProviderService.setUserToken(userToken);
    return this.userService.reloadUser();
  }
}
